import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Save, RefreshCw, Plus, Trash2, Info, Target, Eye } from 'lucide-react';
import { toast } from 'sonner';

export const AboutManager = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await apiService.getSetting(token, 'about');
      setSettings(res.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateSetting(token, 'about', settings);
      toast.success('About page settings saved!');
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const updateWhyChooseUs = (index, field, value) => {
    const items = [...(settings.why_choose_us || [])];
    items[index] = { ...items[index], [field]: value };
    setSettings({ ...settings, why_choose_us: items });
  };

  const addWhyChooseUs = () => {
    setSettings({
      ...settings,
      why_choose_us: [...(settings.why_choose_us || []), { title: '', description: '' }]
    });
  };

  const removeWhyChooseUs = (index) => {
    const items = settings.why_choose_us.filter((_, i) => i !== index);
    setSettings({ ...settings, why_choose_us: items });
  };

  const addImage = () => {
    setSettings({ ...settings, images: [...(settings.images || []), ''] });
  };

  const updateImage = (index, value) => {
    const images = [...settings.images];
    images[index] = value;
    setSettings({ ...settings, images });
  };

  const removeImage = (index) => {
    const images = settings.images.filter((_, i) => i !== index);
    setSettings({ ...settings, images });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="about-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">About Us Page</h1>
          <p className="text-slate-500">Customize your about page content</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Company Description */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Info className="w-5 h-5" /> Company Description</CardTitle>
          <CardDescription>Brief overview of your business</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Company Description</Label>
            <Textarea
              value={settings?.company_description || ''}
              onChange={(e) => setSettings({ ...settings, company_description: e.target.value })}
              placeholder="Brief description of your company..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Business Story</Label>
            <Textarea
              value={settings?.business_story || ''}
              onChange={(e) => setSettings({ ...settings, business_story: e.target.value })}
              placeholder="Tell your business journey and story..."
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* Mission & Vision */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5" /> Mission & Vision</CardTitle>
          <CardDescription>Your company's purpose and goals</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Mission Statement</Label>
            <Textarea
              value={settings?.mission || ''}
              onChange={(e) => setSettings({ ...settings, mission: e.target.value })}
              placeholder="What is your company's mission?"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label>Vision Statement</Label>
            <Textarea
              value={settings?.vision || ''}
              onChange={(e) => setSettings({ ...settings, vision: e.target.value })}
              placeholder="What is your company's vision?"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Why Choose Us */}
      <Card>
        <CardHeader>
          <CardTitle>Why Choose Us</CardTitle>
          <CardDescription>Key reasons customers should choose your business</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings?.why_choose_us?.map((item, index) => (
            <div key={index} className="p-4 border rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Reason {index + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeWhyChooseUs(index)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  value={item.title || ''}
                  onChange={(e) => updateWhyChooseUs(index, 'title', e.target.value)}
                  placeholder="Title (e.g., Quality Materials)"
                />
                <Input
                  value={item.description || ''}
                  onChange={(e) => updateWhyChooseUs(index, 'description', e.target.value)}
                  placeholder="Description"
                />
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addWhyChooseUs}>
            <Plus className="w-4 h-4 mr-1" /> Add Reason
          </Button>
        </CardContent>
      </Card>

      {/* Images */}
      <Card>
        <CardHeader>
          <CardTitle>About Page Images</CardTitle>
          <CardDescription>Images to display on the about page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings?.images?.map((img, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={img}
                onChange={(e) => updateImage(index, e.target.value)}
                placeholder="Image URL"
                className="flex-1"
              />
              <Button variant="ghost" size="icon" onClick={() => removeImage(index)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addImage}>
            <Plus className="w-4 h-4 mr-1" /> Add Image
          </Button>
        </CardContent>
      </Card>

      {/* SEO */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Eye className="w-5 h-5" /> SEO Settings</CardTitle>
          <CardDescription>Search engine optimization for this page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>SEO Title</Label>
            <Input
              value={settings?.seo_title || ''}
              onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })}
              placeholder="Page title for search engines"
            />
          </div>
          <div className="space-y-2">
            <Label>SEO Description</Label>
            <Textarea
              value={settings?.seo_description || ''}
              onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })}
              placeholder="Meta description for search engines"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-sky-500 hover:bg-sky-600">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

export default AboutManager;
